// The sandbox screen's version selector (task 5.3; design **T3**, architecture review **A2**,
// design doc §3.3/§3.3.2, `client-sandbox-screen` "The version selector selects the whole context,
// and its rows are candidates"). Geometry from the visual spec §3.3 (the `.vsel` trigger) and §8.1
// (the open `.menu`), over the sketch `prototype/sketches/sandbox-screen-consolidation/
// empty-and-versions.html`.
//
// This replaces **both** the old version bar's `←` / `→` / `Activate` stepping and the Review
// screen's retired run rail. It is one control because the two were one question asked twice: the rail
// listed runs with no relation to the scripts that produced them, and the bar stepped versions with
// no relation to the runs they produced. Selecting a row here activates the whole document *and*
// loads that version's run together (ROUNDS-MODE §6.2), which is the only way the code view, the
// world view and the type counts can be structurally incapable of disagreeing about which version
// is on screen.
//
// Three properties are load bearing, and each is a rule about what reaches the DOM:
//
//   * **Version-first presentation over candidate identity (A2).** `version-menu-model.ts` already
//     decided the grouping; this file renders that model and re-derives none of it. A version and
//     a run are not one-to-one, so a row shows its version's most recent run and every *other* run
//     of that version stays reachable underneath as a run count that expands. A renderer that drew
//     only `outcome` would silently drop candidates — the exact failure A2 was raised against.
//     Runs the surface could not attribute to any version get their own group rather than being
//     discarded or folded into a version that never produced them.
//   * **Nothing is fabricated (Goal 3).** Every figure on screen is a label the model produced. A
//     version that was never simulated says `not run`; a run that genuinely scored zero says `0`.
//     The one figure the model cannot supply — the chat message that caused an agent version, of
//     which it holds only ids — is rendered from {@link VersionMenuProps.agentMessages} when the
//     caller has the text and as {@link AGENT_MESSAGE_UNAVAILABLE} when it does not, never as an
//     empty line that would read as "there was no message".
//   * **No candidate-selection control exists here.** Submitting or selecting a candidate for
//     grading is `rounds-client`'s, and its absence is structural rather than conventional: this
//     component takes no callback that could carry one and renders no control that could be wired
//     to one. Selecting a run loads a trace; it enters nothing.
//
// **Withheld during a Beekeeper turn.** The server refuses Activate, Run and hand edits while a
// turn is in flight, so the trigger is genuinely `disabled` and the list is not rendered at all.
// This is the deliberate opposite of the Run control's blocked state (design **T6**), which stays
// activatable because pressing it is how the player learns the reason: here there is nothing to
// learn beyond "the Beekeeper is working", and the trigger's title says so without a press.
//
// Deliberate deviation from the sketch: `.mi` is one flat row there, but a version with more than
// one run needs a disclosure control beside the row's own selection control, and a button cannot
// nest inside a button. Each row is therefore a flex container holding a full-width select button
// and — only when it expands — a 44 × 44 disclosure button. Mirrored back into `prototype/`.

import { useState, type CSSProperties, type ReactNode } from 'react';
import type {
  MenuRow,
  MenuRun,
  RunOutcome,
  VersionMenu as VersionMenuModel,
  VersionMenuSort,
} from '../../rounds/version-menu-model';

/** The kit's minimum touch target (`--hit-target`, UI.md §7). Declared as a literal so jsdom can
 * read it off the inline style, matching every other component in this kit. */
const HIT_TARGET = 44;

/** The sketch's `.mi` row height (visual spec §8.1) — comfortably above the hit target, because a
 * row carries two lines of text. */
const ROW_HEIGHT = 52;

/** An agent row's subtitle when the caller did not supply the causing message's text. The quote is
 * the whole content of that line, so leaving it blank would assert that no message caused the
 * version — a different and false fact. This says the message exists and is not loaded. */
export const AGENT_MESSAGE_UNAVAILABLE = 'prompted — message not loaded';

/** The draft's subtitle. It is not a version yet, which is the fact worth stating. */
export const DRAFT_SUBTITLE = 'hand edit · unsaved';

/** What the trigger says when the sandbox has no version history at all to select from. */
export const NO_VERSIONS_LABEL = 'No versions';

export interface VersionMenuProps {
  /** The model from `buildVersionMenu` — rendered as given. */
  menu: VersionMenuModel;
  /** Whether the dropdown is showing. Controlled, so the screen's coordinator owns it (design
   * **T2**) and a version selection cannot leave the menu in a state of its own. */
  open: boolean;
  /** Requests a change of {@link open}. Never called while a turn is in flight. */
  onOpenChange(open: boolean): void;
  /**
   * A row was chosen: activate that version as the working document and load its run together.
   * The caller performs both — this component sequences nothing.
   */
  onSelectRow(row: MenuRow): void;
  /**
   * A specific run under a row was chosen — load that run's trace. `row` is `null` for a run the
   * surface could not attribute to any version. This loads a trace and submits nothing.
   */
  onSelectRun(run: MenuRun, row: MenuRow | null): void;
  /** While a Beekeeper turn is in flight the whole control is withheld. */
  turnInFlight?: boolean;
  /**
   * Which order the rows are in — **controlled**, exactly as {@link open} is, because the ordering
   * is the model's (`buildVersionMenu`'s `sort`) and a second copy held here could disagree with the
   * list actually on screen.
   */
  sort?: VersionMenuSort;
  /** Requests a change of {@link sort}. Absent, the toggle is not rendered at all. */
  onSortChange?(sort: VersionMenuSort): void;
  style?: CSSProperties;
  /** Positioning for the dropdown itself, which the sketch anchors to the control stack's top edge
   * rather than to the trigger (visual spec §8.1). Spread last over the defaults. */
  listStyle?: CSSProperties;
}

/** `Draft`, or `Version 4`. The trigger names the document the editor is on, not the run. */
function triggerLabel(active: MenuRow | undefined): string {
  if (active === undefined) {
    return NO_VERSIONS_LABEL;
  }
  return active.kind === 'draft' ? 'Draft' : `Version ${active.ordinal}`;
}

/** The trigger's trailing readout. A draft that does not compile reports its diagnostics count
 * there, so the blocked state is legible without opening anything (D12). */
function triggerOutcome(active: MenuRow | undefined): { label: string; tone: OutcomeTone } {
  if (active === undefined) {
    return { label: '', tone: 'none' };
  }
  if (active.kind === 'draft' && active.errorCount > 0) {
    return {
      label: `${active.errorCount} ${active.errorCount === 1 ? 'error' : 'errors'}`,
      tone: 'bad',
    };
  }
  return { label: active.outcome.label, tone: toneOf(active.outcome) };
}

type OutcomeTone = 'score' | 'none' | 'bad';

function toneOf(outcome: RunOutcome): OutcomeTone {
  return outcome.kind === 'scored' ? 'score' : 'none';
}

/** Visual spec §3.3 / §8.1: a real score is the accent numeral; an absence is muted italic body
 * text; a diagnostics count is upright danger. The three never share a treatment, because they are
 * three different facts. */
const OUTCOME_STYLE: Record<OutcomeTone, CSSProperties> = {
  score: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 14,
    color: 'var(--accent-strong)',
    fontVariantNumeric: 'tabular-nums',
  },
  none: {
    fontFamily: 'var(--font-body)',
    fontStyle: 'italic',
    fontWeight: 500,
    fontSize: 10,
    color: 'var(--text-muted)',
  },
  bad: {
    fontFamily: 'var(--font-body)',
    fontWeight: 700,
    fontSize: 10,
    color: 'var(--danger)',
  },
};

/**
 * A row's subtitle, or `''` for none.
 *
 * A manual (non-draft) row gets no subtitle at all: the provenance pill already reads "Manual",
 * so a second line repeating it as prose ("edited by hand") said the same fact twice for no
 * added information. The pill stays because it's the scannable one — a glance at the list, not a
 * read of it. An agent row's subtitle is a different fact (which message caused the version), so
 * it stays.
 */
function subtitleOf(row: MenuRow): string {
  if (row.kind === 'draft') {
    return DRAFT_SUBTITLE;
  }
  // **The prompt travels with the version now.** This used to be looked up in a caller-supplied
  // message map that no production caller ever passed, so every agent row read
  // {@link AGENT_MESSAGE_UNAVAILABLE} in the shipped client. The prompt is denormalised onto the
  // version row at commit, so the row already holds the one thing the map was for — and the map is
  // gone rather than left as an unused second source.
  if (row.prompt !== null && row.prompt !== '') {
    return `“${row.prompt}”`;
  }
  if (row.provenance !== 'agent') {
    return '';
  }
  return AGENT_MESSAGE_UNAVAILABLE;
}

/** The two orders, in the order they are offered. `recent` first because it is the default and the
 * one a player wanting to carry on working reaches for. */
const SORT_OPTIONS: readonly { value: VersionMenuSort; label: string }[] = [
  { value: 'recent', label: 'Most recent' },
  { value: 'score', label: 'Top score' },
];

const PROVENANCE_STYLE: Record<'agent' | 'manual', CSSProperties> = {
  agent: { background: 'var(--info-soft)', color: 'var(--info)' },
  manual: { background: 'var(--paper-2)', color: 'var(--ink-2)' },
};

/** One run under an expanded row, or one the surface could not attribute. It carries the run's own
 * identity and its own score — that is the whole reason it is on screen. */
function RunEntry({
  run,
  onSelect,
}: {
  run: MenuRun;
  onSelect(): void;
}): ReactNode {
  return (
    <button
      type="button"
      data-testid={`version-menu-run-${run.runId}`}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        width: '100%',
        minHeight: HIT_TARGET,
        padding: '0 11px 0 44px',
        border: 'none',
        borderTop: '1px solid var(--line)',
        background: 'var(--paper-1)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontFamily: 'var(--font-body)',
          fontWeight: 600,
          fontSize: 11.5,
          color: 'var(--ink-2)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Run {run.runId}
      </span>
      <span
        style={{
          ...OUTCOME_STYLE[run.score === null ? 'none' : 'score'],
          width: 44,
          textAlign: 'right',
          flex: 'none',
        }}
      >
        {run.scoreLabel}
      </span>
    </button>
  );
}

export function VersionMenu({
  menu,
  open,
  onOpenChange,
  onSelectRow,
  onSelectRun,
  turnInFlight = false,
  sort = 'recent',
  onSortChange,
  style,
  listStyle,
}: VersionMenuProps) {
  // Which rows have their runs revealed. Purely presentational — expanding shows candidates that
  // already exist in the model, so it is local state rather than the coordinator's (design T2).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const active = menu.rows.find((row) => row.isActive);
  const trigger = triggerOutcome(active);
  const withheld = turnInFlight;

  return (
    <div
      data-testid="version-menu"
      style={{ position: 'relative', flex: 1, minWidth: 0, ...style }}
    >
      <button
        type="button"
        data-testid="version-menu-trigger"
        aria-expanded={open && !withheld}
        aria-haspopup="true"
        disabled={withheld || menu.rows.length === 0}
        title={
          withheld
            ? 'The Beekeeper is working — versions are unavailable until the turn finishes'
            : 'Choose a version'
        }
        onClick={() => onOpenChange(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          height: HIT_TARGET,
          padding: '0 10px',
          borderRadius: 'var(--r-md)',
          border: '1.5px solid var(--line-strong)',
          background: open && !withheld ? 'var(--honey-100)' : 'var(--surface-card)',
          fontFamily: 'var(--font-display)',
          fontWeight: 500,
          fontSize: 13,
          color: 'var(--ink-1)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          cursor: withheld ? 'default' : 'pointer',
          opacity: withheld ? 0.38 : 1,
        }}
      >
        {active?.kind === 'draft' ? (
          <span
            style={{
              fontFamily: 'var(--font-body)',
              fontWeight: 800,
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: 'var(--tracking-caps)',
              color: 'var(--accent-strong)',
              background: 'var(--accent-soft)',
              borderRadius: 'var(--r-pill)',
              padding: '2px 7px',
            }}
          >
            Draft
          </span>
        ) : (
          <span>{triggerLabel(active)}</span>
        )}
        <span style={{ ...OUTCOME_STYLE[trigger.tone], marginLeft: 'auto' }}>{trigger.label}</span>
        <span aria-hidden="true" style={{ color: 'var(--ink-3)', fontSize: 10, flex: 'none' }}>
          {open && !withheld ? '▴' : '▾'}
        </span>
      </button>

      {open && !withheld ? (
        <div
          data-testid="version-menu-list"
          role="group"
          aria-label="Versions"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 'calc(100% + 10px)',
            background: 'var(--surface-card)',
            border: '1.5px solid var(--hud-line)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 -6px 20px rgba(43,33,20,.22)',
            // **The list scrolls; it does not clip** (WAG-91). `overflow: hidden` was the right rule
            // for a list of two or three contenders and the wrong one for a version history, which is
            // what this menu now shows: rows past the panel's height were drawn nowhere and reachable
            // by nothing. The cross-axis stays hidden so a long summary still ellipsises rather than
            // producing a horizontal scrollbar, and the caller's own `maxHeight` (which is what makes
            // this axis overflow at all) rides in on `listStyle`.
            overflowX: 'hidden',
            overflowY: 'auto',
            zIndex: 25,
            ...listStyle,
          }}
        >
          {/* **The sort toggle.** Two segments rather than a dropdown: there are exactly two orders
              and both fit, so the current one is readable without a press. `aria-pressed` on each,
              because they are the same control in two states, not two actions. */}
          {onSortChange !== undefined && (
            <div
              role="group"
              aria-label="Sort versions"
              data-testid="version-menu-sort"
              style={{
                display: 'flex',
                gap: 4,
                padding: '7px 8px',
                borderBottom: '1px solid var(--line)',
                background: 'var(--paper-1)',
              }}
            >
              {SORT_OPTIONS.map(({ value, label }) => {
                const selected = sort === value;
                return (
                  <button
                    key={value}
                    type="button"
                    data-testid={`version-menu-sort-${value}`}
                    aria-pressed={selected}
                    onClick={() => onSortChange(value)}
                    style={{
                      flex: 1,
                      minHeight: 32,
                      borderRadius: 'var(--r-pill)',
                      border: `1px solid ${selected ? 'var(--accent-strong)' : 'var(--line)'}`,
                      background: selected ? 'var(--accent-soft)' : 'var(--surface-card)',
                      color: selected ? 'var(--accent-strong)' : 'var(--ink-2)',
                      fontFamily: 'var(--font-body)',
                      fontWeight: selected ? 800 : 600,
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: 'var(--tracking-caps)',
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          {menu.rows.map((row) => {
            const isOpen = expanded.has(row.id);
            const tone: OutcomeTone =
              row.kind === 'draft' && row.errorCount > 0 ? 'none' : toneOf(row.outcome);
            return (
              <div key={row.id} style={{ borderBottom: '1px solid var(--line)' }}>
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <button
                    type="button"
                    data-testid={`version-menu-row-${row.id}`}
                    aria-pressed={row.isActive}
                    onClick={() => onSelectRow(row)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      flex: 1,
                      minWidth: 0,
                      minHeight: ROW_HEIGHT,
                      padding: '7px 11px',
                      border: 'none',
                      background: row.isActive ? 'var(--honey-100)' : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span
                      style={{
                        width: 24,
                        flex: 'none',
                        fontFamily: 'var(--font-display)',
                        fontWeight: 600,
                        fontSize: 13,
                        color: 'var(--ink-1)',
                      }}
                    >
                      {row.ordinalLabel}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <b
                        style={{
                          display: 'block',
                          fontFamily: 'var(--font-body)',
                          fontWeight: 600,
                          fontSize: 12.5,
                          color: 'var(--ink-1)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.summary}
                      </b>
                      {/* Omitted entirely, not rendered empty, when there's nothing to add beyond
                          the provenance pill (see `subtitleOf`). */}
                      {subtitleOf(row) !== '' && (
                        <span
                          style={{
                            display: 'block',
                            fontFamily: 'var(--font-body)',
                            fontSize: 10,
                            color: 'var(--text-muted)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {subtitleOf(row)}
                        </span>
                      )}
                    </span>
                    <span
                      data-testid={`version-menu-provenance-${row.id}`}
                      style={{
                        flex: 'none',
                        fontFamily: 'var(--font-body)',
                        fontWeight: 800,
                        fontSize: 8,
                        textTransform: 'uppercase',
                        letterSpacing: 'var(--tracking-caps)',
                        borderRadius: 'var(--r-pill)',
                        padding: '2px 6px',
                        ...PROVENANCE_STYLE[row.kind === 'draft' ? 'manual' : row.provenance],
                      }}
                    >
                      {row.provenanceLabel}
                    </span>
                    <span
                      data-testid={`version-menu-outcome-${row.id}`}
                      style={{
                        ...OUTCOME_STYLE[tone],
                        width: 44,
                        textAlign: 'right',
                        flex: 'none',
                      }}
                    >
                      {row.outcome.label}
                    </span>
                  </button>

                  {row.runCount > 1 ? (
                    <button
                      type="button"
                      data-testid={`version-menu-runs-${row.id}`}
                      aria-expanded={isOpen}
                      title={`${row.runCount} runs of ${row.ordinalLabel}`}
                      onClick={() =>
                        setExpanded((current) => {
                          const next = new Set(current);
                          if (!next.delete(row.id)) {
                            next.add(row.id);
                          }
                          return next;
                        })
                      }
                      style={{
                        width: HIT_TARGET,
                        minHeight: HIT_TARGET,
                        flex: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        padding: '0 2px',
                        border: 'none',
                        borderLeft: '1px solid var(--line)',
                        background: row.isActive ? 'var(--honey-100)' : 'transparent',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-body)',
                        fontWeight: 700,
                        fontSize: 9,
                        color: 'var(--ink-3)',
                      }}
                    >
                      <span>{row.runCount} runs</span>
                      <span aria-hidden="true" style={{ fontSize: 10 }}>
                        {isOpen ? '▾' : '▸'}
                      </span>
                    </button>
                  ) : null}
                </div>

                {isOpen
                  ? row.runs.map((run) => (
                      <RunEntry key={run.id} run={run} onSelect={() => onSelectRun(run, row)} />
                    ))
                  : null}
              </div>
            );
          })}

          {menu.unattributedRuns.length > 0 ? (
            <div>
              <h5
                style={{
                  margin: 0,
                  padding: '7px 11px 3px',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 800,
                  fontSize: 8,
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  color: 'var(--text-muted)',
                }}
              >
                Runs with no version on record
              </h5>
              {menu.unattributedRuns.map((run) => (
                <RunEntry key={run.id} run={run} onSelect={() => onSelectRun(run, null)} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
